import { Vue } from 'vue-property-decorator'
import { LoginApi } from '@/api/methods/LoginApi'
import { ApiResponseLogin } from '@/api/responses/login'
import { ApiRequireResetPasswordAppError } from '@/models/AppErrorClasses'
import { UserRole } from '@/const/UserRole'

/**
 * ログイン処理
 */
export default class LoginMethods {
  /** 二重呼び出し防止用フラグ */
  private static loginApiCalled = false

  /**
   * 生徒用ログイン
   *
   * @param loginId ログインID
   * @param password パスワード
   * @returns ログイン結果(成功/失敗、二重呼び出しによって処理がスキップされた場合はnullを返す)
   */
  public static async studentLogin(loginId: string, password: string): Promise<boolean | null> {
    return await this.login(loginId, password, UserRole.STUDENT)
  }

  /**
   * 先生・教室長ログイン
   *   現状では教室長権限を独自に定義する必要ないため、共通で使用する
   *
   * @param loginId ログインID
   * @param password パスワード
   * @returns ログイン結果(成功/失敗、二重呼び出しによって処理がスキップされた場合はnullを返す)
   */
  public static async teacherLogin(loginId: string, password: string): Promise<boolean | null> {
    return await this.login(loginId, password, UserRole.TEACHER)
  }

  /**
   * 塾管理者用ログイン
   *
   * @param loginId ログインID
   * @param password パスワード
   * @returns ログイン結果(成功/失敗、二重呼び出しによって処理がスキップされた場合はnullを返す)
   */
  public static async academyLogin(loginId: string, password: string): Promise<boolean | null> {
    return await this.login(loginId, password, UserRole.ACADEMY)
  }

  /**
   * システム管理者用ログイン
   *
   * @param loginId ログインID
   * @param password パスワード
   * @returns ログイン結果(成功/失敗、二重呼び出しによって処理がスキップされた場合はnullを返す)
   */
  public static async adminLogin(loginId: string, password: string): Promise<boolean | null> {
    return await this.login(loginId, password, UserRole.ADMIN)
  }

  /**
   * ログイン処理
   *
   * @param loginId ログインID
   * @param password パスワード
   * @param role ログイン権限
   * @returns ログイン結果(成功/失敗、二重呼び出しによって処理がスキップされた場合はnullを返す)
   */
  private static async login(loginId: string, password: string, role: string): Promise<boolean | null> {
    // 二重呼び出し防止
    if (this.loginApiCalled) {
      return null
    }
    this.loginApiCalled = true

    // cookieの認証情報を削除
    Vue.prototype.$appCookies.removeAuth()

    let loginResponse!: ApiResponseLogin

    try {
      // API実行
      loginResponse = await LoginApi.login(loginId, password, role)
      // cookieに認証情報を保存
      await Vue.prototype.$appCookies.auth.set({
        accessToken: loginResponse.token,
        uid: loginResponse.uid,
        client: loginResponse.client,
      })
      // 権限ごとの情報をcookieに保存
      await this.setRoleCookies(loginResponse)
    } catch {
      return false
    } finally {
      // 再実行可能にする
      this.loginApiCalled = false
    }

    // パスワード再設定要求がされている場合、例外を発生させる
    if (this.isRequireResetPassword(loginResponse)) {
      throw new ApiRequireResetPasswordAppError()
    }

    return true
  }

  /**
   * 権限ごとのログイン時情報をCookieに設定する
   *
   * @param loginResponse ログインAPIのレスポンス情報
   */
  private static async setRoleCookies(loginResponse: ApiResponseLogin): Promise<void> {
    // 権限ごとの情報が存在する場合のみcookieに設定する
    if (loginResponse.student) {
      // 生徒情報の設定
      await Vue.prototype.$appCookies.authStudent.set({
        userId: loginResponse.student.userId,
        nickname: loginResponse.student.nickname,
        loginId: loginResponse.student.loginId,
        gradeCode: loginResponse.student.gradeCode,
        academyName: loginResponse.student.academyName,
        branchName: loginResponse.student.branchName,
        avatarIconCode: loginResponse.student.avatarIconCode,
        lastLoginDate: loginResponse.student.lastLoginDate,
      })
    }
    if (loginResponse.teacher) {
      // 先生情報の設定
      await Vue.prototype.$appCookies.authTeacher.set({
        userId: loginResponse.teacher.userId,
        isAdmin: loginResponse.teacher.isAdmin,
        nickname: loginResponse.teacher.nickname,
        loginId: loginResponse.teacher.loginId,
        academyCode: loginResponse.teacher.academyCode,
        academyName: loginResponse.teacher.academyName,
        branchId: loginResponse.teacher.branchId,
        branchName: loginResponse.teacher.branchName,
        avatarIconCode: loginResponse.teacher.avatarIconCode,
      })
    }
    if (loginResponse.academy) {
      // 塾管理者情報の設定
      await Vue.prototype.$appCookies.authAcademy.set({
        userId: loginResponse.academy.userId,
        nickname: loginResponse.academy.nickname,
        loginId: loginResponse.academy.loginId,
        academyId: loginResponse.academy.academyId,
        academyCode: loginResponse.academy.academyCode,
        academyName: loginResponse.academy.academyName,
        avatarIconCode: loginResponse.academy.avatarIconCode,
      })
    }
    if (loginResponse.admin) {
      // システム管理者情報の設定
      await Vue.prototype.$appCookies.authAdmin.set({
        userId: loginResponse.admin.userId,
        nickname: loginResponse.admin.nickname,
        loginId: loginResponse.admin.loginId,
        avatarIconCode: loginResponse.admin.avatarIconCode,
      })
    }
  }

  /**
   * パスワード再設定要求がされているか
   *
   * @param loginResponse ログインAPIのレスポンス情報
   * @returns 再設定要求の有無
   */
  private static isRequireResetPassword(loginResponse: ApiResponseLogin): boolean {
    if (loginResponse.student) {
      // 生徒権限の場合、レスポンス情報の値を返す
      return loginResponse.student.requireResetPassword
    }

    // 該当しない場合はfalse
    return false
  }
}
